/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// react-countup components
import CountUp from "react-countup";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

function StatsCounterCard({ color = "success", count, title, description = "", prefix = "", suffix = "", height = "", transparent = false }) {

    switch (title) {
        case "Battery":
            if (count <= 20) {
                color = "error";
            }
            else if (count > 20 && count <= 60) {
                color = "warning";
            }
            break;
        case "Humidity":
            if (count > 80) {
                color = "error";
            }
            else if (count > 55) {
                color = "warning";
            }
            break;
        case "Temperature":
            if (count > 60 || count < -5) {
                color = "error";
            }
            else if (count > 45 || count < 5) {
                color = "warning";
            }
            else if (count <= 45 || count >= 5) {
                color = "success";
            }
            break;
        case "Signal Strength":
            if (count > 85) {
                color = "error";
            }
            else if (count > 70) {
                color = "warning";
            }
            break;
        case "Difference Since Last Hour":
            if (count < 0) {
                color = "error";
            }
            else if (count > 0) {
                color = "success";
            }
            else {
                color = "secondary";
            }
            break;
        default:
            color = "secondary";
            break;
    }

    if (height) {

        const content = (
            <SoftBox height="100%" p={3} textAlign="center" lineHeight={1.25} display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                <SoftTypography variant="h1" color={color} fontWeight="bold" textGradient>
                    {prefix && (
                        <SoftTypography component="span" variant="h5" fontWeight="bold">
                            {prefix}
                        </SoftTypography>
                    )}
                    <SoftBox display="inline-block" mx={0.5}>
                        <CountUp end={count} duration={1} separator="," />
                    </SoftBox>
                    {suffix && (
                        <SoftTypography component="span" variant="h5" fontWeight="bold">
                            {suffix}
                        </SoftTypography>
                    )}
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" textTransform="capitalize">
                    {title}
                </SoftTypography>
                {description && (
                    <SoftTypography
                        variant="button"
                        fontWeight="regular"
                        opacity={0.8}
                        textTransform="capitalize"
                    >
                        {description}
                    </SoftTypography>
                )}
            </SoftBox>
        )

        if (transparent) {
            return content;
        }
        else {
            return (
                <Card sx={{ height: '100%' }}>
                    {content}
                </Card>
            );
        }
    }
    else {

        const content = (
            <SoftBox p={3} textAlign="center" lineHeight={1.25}>
                <SoftTypography variant="h1" color={color} fontWeight="bold" textGradient>
                    {prefix && (
                        <SoftTypography component="span" variant="h5" fontWeight="bold">
                            {prefix}
                        </SoftTypography>
                    )}
                    <SoftBox display="inline-block" mx={0.5}>
                        <CountUp end={count} duration={1} separator="," />
                    </SoftBox>
                    {suffix && (
                        <SoftTypography component="span" variant="h5" fontWeight="bold">
                            {suffix}
                        </SoftTypography>
                    )}
                </SoftTypography>
                <SoftTypography variant="h6" fontWeight="bold" textTransform="capitalize">
                    {title}
                </SoftTypography>
                {description && (
                    <SoftTypography
                        variant="button"
                        fontWeight="regular"
                        opacity={0.8}
                        textTransform="capitalize"
                    >
                        {description}
                    </SoftTypography>
                )}
            </SoftBox>
        );

        if (transparent) {
            return content;
        }
        else {
            return (
                <Card>
                    {content}
                </Card>
            );
        }
    }
}

// Typechecking props for the BlogCard
StatsCounterCard.propTypes = {
    color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
    count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    height: PropTypes.string,
    transparent: PropTypes.bool,
};

export default StatsCounterCard;
